import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ENV } from '@app/constants/global.constants';
import { Env } from '@app/types/env';
import { JengaError } from '@app/types/error';
import ContentDTO, { ContentDataHoraModificacioDTO } from '@data/models/content/content-dto';
import { BehaviorSubject, Observable, Subject, forkJoin, from, of, throwError } from 'rxjs';
import { catchError, finalize, map, take } from 'rxjs/operators';
import { AuthenticationService } from '@app/security/authentication.service';
import { DbProgressiveDownloadService } from './dbDownloadProgress.service';
import { ProgressSpinnerDialogService } from '@shared/services/progress-spinner-dialog.service';
import { StatesConstants } from '@app/constants/states.constants';
import {
  ViewTypesConstants,
  isClinicGuideView,
  isDirectContentView,
  isPdfListView,
  isThematicBlockView
} from '@app/constants/viewTypes.constants';
import { eliminarDiacriticos } from '@shared/utils/string-normalization-lower-case';
import { ContentSortToolService } from './content-sort-tool.service';
import { IMAGE_QUALITY_LIST } from '@app/constants/imatgeQuality';
import ImatgeDTO from '@data/models/content/imatge-dto';

@Injectable({
  providedIn: 'root'
})
export class ContentService {
  public contentViewChanges: Subject<{
    view: ViewTypesConstants;
    onlyIndexedDb: boolean;
    removingContent?: ContentDTO;
  }> = new Subject();
  public indexedDBContent: Subject<{
    view: ViewTypesConstants;
    content: ContentDTO[];
  }> = new Subject();

  public resetObsDataLoaded$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private readonly API_PATH = '/api';
  private readonly DATA_HORA_MODIFICACIO = '/data-hora-modificacio';
  private readonly GUIES_CLINIQUES_PATH = '/guies-cliniques';
  private readonly CODIS_ACTIVACIO_PATH = '/codis-activacio';
  private readonly REPOSITORI_PATH = '/repositori';
  private readonly FILTER_PATH = '/filtrar';
  private readonly ORDEN_PATH = '/orden';
  private readonly MAX_SIZE_PATH = '/max-size-file';

  constructor(
    @Inject(ENV) private env: Env,
    private http: HttpClient,
    private authService: AuthenticationService,
    public progressiveDownloadService: DbProgressiveDownloadService,
    private spinnerService: ProgressSpinnerDialogService,
    private contentSortToolService: ContentSortToolService
  ) {}

  public triggerObsDataReload(): void {
    this.resetObsDataLoaded$.next(true);
  }

  public getContentDetailsById(view: ViewTypesConstants, id: number, versio: number): Observable<ContentDTO> {
    return new Observable((observer) => {
      this.progressiveDownloadService.semDB[view]
        .get([id, versio])
        .then((result) => {
          if (result) {
            observer.next(result);
          } else {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const filter: any = {
              ids: [{ id, versio }],
              tipus: null
            };
            if (view === 'CECOS' || view === 'SVB' || view === 'SVA') {
              filter.tipus = [view];
            }
            this.filterContentByViewAndUpdateIndexedDB(view, filter)
              .pipe(take(1))
              .subscribe({
                next: (c: ContentDTO[]) => {
                  if (c?.length) {
                    observer.next(c[0]);
                  } else {
                    this.progressiveDownloadService.semDB[view]
                      .where('id')
                      .equals(id)
                      .toArray()
                      .then((resultsById: ContentDTO[]) => {
                        if (resultsById?.length) {
                          const publishContent = resultsById.find((cont) => cont.estat === StatesConstants.PUB);
                          if (publishContent) {
                            //Si hay una versión publicada devolvemos esa
                            observer.next(publishContent);
                          } else {
                            //Devolvemos la última versión
                            observer.next(resultsById.sort((a, b) => b.versio - a.versio)[0]);
                          }
                        } else {
                          observer.next(null);
                        }
                      });
                  }
                },
                error: (e) => {
                  observer.error(e);
                }
              });
          }
        })
        .catch((error) => {
          console.error('Error al recuperar datos de IndexedDB', error);
          observer.next(null);
        });
    });
  }

  public searchInAllContent = (text?: string, path?: string): Promise<{ [key: string]: ContentDTO[] }> => {
    const contentsToReturn: { [key: string]: ContentDTO[] } = {};
    let viewsToSearch: string[] = [];
    if (path) {
      let view = path;
      if (view === 'USVA') {
        view = 'SVA';
      } else if (view === 'USVB') {
        view = 'SVB';
      }
      viewsToSearch = [view];
    } else {
      Object.values(ViewTypesConstants).forEach((view: ViewTypesConstants) => {
        viewsToSearch.push(view);
      });
    }
    return new Promise((resolve) => {
      const requests: Promise<ContentDTO[]>[] = [];
      viewsToSearch.forEach((view: ViewTypesConstants) => {
        contentsToReturn[view] = [];
        requests.push(this.getDirectContentPublished(view));
      });
      forkJoin(requests).subscribe({
        next: (results) => {
          viewsToSearch.forEach((view: ViewTypesConstants, index: number) => {
            contentsToReturn[view] = this.filterContentArray(results[index], text).sort(
              this.contentSortToolService.sortByTitolAndVersion
            );
          });
          resolve(contentsToReturn);
        },
        error: (err) => {
          console.error(err);
          resolve(contentsToReturn);
        }
      });
    });
  };

  public filterContentArray(contents: ContentDTO[], text: string): ContentDTO[] {
    while (text.includes('  ')) {
      text = text.split('  ').join(' ');
    }
    const arrWords = text.trim().split(' ');
    contents = contents.filter((content: ContentDTO) => {
      const match: boolean = arrWords.reduce((acc, word) => {
        const lowerWord = eliminarDiacriticos(word.trim().toLowerCase());
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        if (acc) {
          acc =
            eliminarDiacriticos(content.titol)?.toLowerCase().includes(lowerWord) ||
            eliminarDiacriticos(content.blocTematic?.nom ? content.blocTematic?.nom : '')
              ?.toLowerCase()
              .includes(lowerWord) ||
            // eslint-disable-next-line max-len
            eliminarDiacriticos(this.getPlainTextFromHtml(content.document))?.toLowerCase().includes(lowerWord) ||
            content.versio?.toString().includes(eliminarDiacriticos(lowerWord)) ||
            eliminarDiacriticos(content.dataPublicacio)?.toLowerCase().includes(lowerWord) ||
            eliminarDiacriticos(content.dataEliminacio)?.toLowerCase().includes(lowerWord);
        }
        return acc;
      }, true);
      return match;
    });
    return contents;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public getPlainTextFromHtml(html: any) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    return doc.body.textContent || doc.body.innerText;
  }

  /**
   * Devuelve todo el contenido asociado a una vista
   *
   * @param view :  ViewTypesConstants
   * @param allFields: boolean => si está a false devuelve sólo id, versión, título y estado
   * @returns ContentDTO[]
   */
  public getContentByView(
    view: ViewTypesConstants,
    allFields = true,
    onlyFromIndexedDB = false,
    onlyPubState = false,
    checkAllStatesInDataHoraDarreraModificacio = false,
    loadObs = false
  ): Observable<{ content: ContentDTO[]; paginated: boolean }> {
    //En el readme hay más información respecto al flujo.
    const observable: Observable<{ content: ContentDTO[]; paginated: boolean }> = new Observable((observer) => {
      //Obtengo el rol del usuario y defino el tipo de contenido que debo devolver
      const contentStateToView = this.authService.getUserRole() === 'VISUALITZADOR' || onlyPubState
        ? [StatesConstants.PUB, StatesConstants.ESB] : null;
      //Comprobamos si tiene información almacenada en la indexedDB
      this.progressiveDownloadService.semDB[view].toArray().then(
        (allContent: ContentDTO[]) => {
          if (onlyFromIndexedDB) {
            if (contentStateToView) {
              allContent = allContent.filter((c) => contentStateToView.includes(c.estat));
            }
            if (allFields) {
              observer.next({ content: allContent, paginated: false });
            } else {
              observer.next({
                content: allContent.map(
                  (value: ContentDTO) =>
                    ({
                      id: value.id,
                      versio: value.versio,
                      titol: value.titol,
                      estat: value.estat,
                      ordre: value.ordre,
                      dataPublicacio: value.dataPublicacio,
                      dataHoraDarreraModificacio: value.dataHoraDarreraModificacio,
                      imatge: isPdfListView(view) ? value.imatge : null,
                      blocTematic: isThematicBlockView(view) ? value.blocTematic : null,
                      hrm: value.hrm ? value.hrm : null
                    } as ContentDTO)
                ),
                paginated: false
              });
            }
          } else if (allContent.length && !this.progressiveDownloadService.isDownlodingDataForTable(view)) {
            //Tengo contenido y no se está descargando progresivamente los datos => informo a la vista
            let indexedDBContent: ContentDTO[] = [...allContent];
            if (contentStateToView) {
              indexedDBContent = indexedDBContent.filter((c) => contentStateToView.includes(c.estat));
          }
            if (allFields) {
              this.indexedDBContent.next({ view, content: indexedDBContent });
            } else {
              this.indexedDBContent.next({
                view,
                content: indexedDBContent.map(
                  (value: ContentDTO) =>
                    ({
                      id: value.id,
                      versio: value.versio,
                      titol: value.titol,
                      estat: value.estat,
                      ordre: value.ordre,
                      dataPublicacio: value.dataPublicacio,
                      dataHoraDarreraModificacio: value.dataHoraDarreraModificacio,
                      imatge: isPdfListView(view) ? value.imatge : null,
                      blocTematic: isThematicBlockView(view) ? value.blocTematic : null,
                      hrm: value.hrm ? value.hrm : null
                    } as ContentDTO)
                )
              });
            }
            //Si tengo datos en la indexed actualizo la DB cotejando las fechas de los registros.
            this.checkContentDateHourModification(
              view,
              allContent,
              checkAllStatesInDataHoraDarreraModificacio ? null : contentStateToView,
              loadObs
            )
              .pipe(
                take(1),
                finalize(() => this.progressiveDownloadService.checkStorageQuota())
              )
              .subscribe({
                next: (data: ContentDTO[]) => {
                  if (allFields) {
                    observer.next({
                      content: data.filter((c) => {
                        if (contentStateToView) {
                          return contentStateToView.includes(c.estat);
                        }
                        return true;
                      }),
                      paginated: false
                    });
                  } else {
                    observer.next({
                      content: data
                        .map(
                          (value: ContentDTO) =>
                            ({
                              id: value.id,
                              versio: value.versio,
                              titol: value.titol,
                              estat: value.estat,
                              ordre: value.ordre,
                              dataPublicacio: value.dataPublicacio,
                              dataHoraDarreraModificacio: value.dataHoraDarreraModificacio,
                              imatge: isPdfListView(view) ? value.imatge : null,
                              blocTematic: isThematicBlockView(view) ? value.blocTematic : null,
                              hrm: value.hrm ? value.hrm : null
                            } as ContentDTO)
                        )
                        .filter((c) => {
                          if (contentStateToView) {
                            return contentStateToView.includes(c.estat);
                          }
                          return true;
                        }),
                      paginated: false
                    });
                  }
                },
                error: (err) => {
                  console.log(err);
                  if (allFields) {
                    observer.next({ content: allContent, paginated: false });
                  } else {
                    observer.next({
                      content: allContent
                        .map(
                          (value: ContentDTO) =>
                            ({
                              id: value.id,
                              versio: value.versio,
                              titol: value.titol,
                              estat: value.estat,
                              ordre: value.ordre,
                              dataPublicacio: value.dataPublicacio,
                              dataHoraDarreraModificacio: value.dataHoraDarreraModificacio,
                              imatge: isPdfListView(view) ? value.imatge : null,
                              blocTematic: isThematicBlockView(view) ? value.blocTematic : null,
                              hrm: value.hrm ? value.hrm : null
                            } as ContentDTO)
                        )
                        .filter((c) => {
                          if (contentStateToView) {
                            return contentStateToView.includes(c.estat);
                          }
                          return true;
                        }),
                      paginated: false
                    });
                  }
                }
              });
          } else if (allContent.length) {
            //Hay contenido pero se está obteniendo todavía => obtenerlo de forma paginada
            observer.next({
              content: allContent.filter((c) => {
                if (contentStateToView) {
                  return contentStateToView.includes(c.estat);
                }
                return true;
              }),
              paginated: true
            });
          } else {
            //No hay contenido => obtenerlo de forma paginada
            this.progressiveDownloadService.fetchData(view).then(() => {
              // Esperar a que se complete la sincronización inicial
              this.progressiveDownloadService.semDB[view].toArray().then((syncedContent) => {
                observer.next({ content: syncedContent, paginated: true });
              });
            });
          }
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (err: any) => {
          console.error(err);
          observer.error(err);
        }
      );
    });
    return observable;
  }

  public changeOrder(view: ViewTypesConstants, contents: ContentDTO[]): Observable<ContentDTO[]> {
    //DGDC Añadir función que invoca a back cuando funcione
    // console.log('DGDC: falta invocar a back para guardar los cambios ', view, contents);
    if (view && contents.length) {
      let viewPath = `/${view}`;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (isClinicGuideView(view) || isDirectContentView(view)) {
        viewPath = this.GUIES_CLINIQUES_PATH;
      } else if (view === ViewTypesConstants.codisActivacio) {
        viewPath = this.CODIS_ACTIVACIO_PATH;
      } else if (view === ViewTypesConstants.repositori) {
        viewPath = this.REPOSITORI_PATH;
      }
      return (
        this.http
          .put<ContentDTO[]>(
            `${this.env.apiBaseUrl}${this.API_PATH}${viewPath}${this.ORDEN_PATH}`,
            contents.map((b) => ({ id: b.id, versio: b.versio, ordre: b.ordre }))
          )
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .pipe(catchError((err: any) => throwError(err.error as JengaError)))
      );
    }
    return of(null);
  }

  public addContent(view: ViewTypesConstants, contentData: ContentDTO, avoidServerRequest?: boolean): Observable<ContentDTO> {
    if (view) {
      // console.log(contentData);
      let path = `/${view}`;
      if (isClinicGuideView(view) || isDirectContentView(view)) {
        path = this.GUIES_CLINIQUES_PATH;
      } else if (view === ViewTypesConstants.codisActivacio) {
        path = this.CODIS_ACTIVACIO_PATH;
      } else if (view === ViewTypesConstants.repositori) {
        path = this.REPOSITORI_PATH;
      }
      return this.http.post<ContentDTO>(`${this.env.apiBaseUrl}${this.API_PATH}${path}`, contentData).pipe(
        map((data: ContentDTO) => {
          if (data?.imatges?.length) {
            data.imatges = data.imatges.filter(
              (img) => img.resolucio === 'FHD' || !data.imatges.find((i) => i.nomArxiu === img.nomArxiu && i.resolucio === 'FHD')
            );
          }
          this.progressiveDownloadService.semDB[view]
            .put(data)
            .then(() => {
              this.contentViewChanges.next({ view, onlyIndexedDb: !avoidServerRequest });
            })
            .catch((error) => console.error('Error al almacenar datos en IndexedDB', view, error));
          return data;
        }),
        catchError((error) => throwError(error as JengaError))
      );
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public deleteContent(view: ViewTypesConstants, contentData: ContentDTO): Observable<any> {
    let viewPath = `/${view}`;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (isClinicGuideView(view) || isDirectContentView(view)) {
      viewPath = this.GUIES_CLINIQUES_PATH;
    } else if (view === ViewTypesConstants.codisActivacio) {
      viewPath = this.CODIS_ACTIVACIO_PATH;
    } else if (view === ViewTypesConstants.repositori) {
      viewPath = this.REPOSITORI_PATH;
    }
    return (
      this.http
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .delete<any>(`${this.env.apiBaseUrl}${this.API_PATH}${viewPath}/${contentData.id}/${contentData.versio}`)
        .pipe(catchError((error) => throwError(error.error as JengaError)))
    );
  }

  public async getDirectContentPublished(view: ViewTypesConstants): Promise<ContentDTO[]> {
    let contentPublished: ContentDTO[] = [];
    await this.progressiveDownloadService.semDB[view].toArray().then((contents: ContentDTO[]) => {
        contents = contents.filter((c) => c.estat === 'PUB' || c.estat === 'ESB');
        contentPublished = contents;
    });
    return contentPublished;
  }

  public getMaxSizeFile(): Observable<number> {
    return (
      this.http
        .get<number>(
          `${this.env.apiBaseUrl}${this.API_PATH}${this.REPOSITORI_PATH}${this.MAX_SIZE_PATH}`
        )
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .pipe(catchError((err: any) => throwError(err.error as JengaError)))
    );
  }

  private checkContentDateHourModification(
    view: ViewTypesConstants,
    allContent: ContentDTO[],
    states: StatesConstants[],
    loadObs: boolean
  ): Observable<ContentDTO[]> {
    return new Observable((observer) => {
      let request: Observable<ContentDataHoraModificacioDTO[]> = null;
      let param = `?tipus=${view}`;

      if (states && states.length > 0) {
        const statesParam = states.map(state => `estat=${state}`).join('&');
        param += `&${statesParam}`;
      } else {
        const defaultStates = [StatesConstants.PUB, StatesConstants.ESB];
        const statesParam = defaultStates.map(state => `estat=${state}`).join('&');
        param += `&${statesParam}`;
      }

      // Si loadObs es true, añadir el estado OBS al parámetro
      if (loadObs) {
        param += `&estat=${StatesConstants.OBS}`;
      }

      if (isClinicGuideView(view) || isDirectContentView(view)) {
        request = this.http.get<ContentDataHoraModificacioDTO[]>(
          `${this.env.apiBaseUrl}${this.API_PATH}${this.GUIES_CLINIQUES_PATH}${this.DATA_HORA_MODIFICACIO}${param}`
        );
      } else {
        let path = `/${view}`;
        if (view === ViewTypesConstants.codisActivacio) {
          path = this.CODIS_ACTIVACIO_PATH;
        } else if (view === ViewTypesConstants.repositori) {
          path = this.REPOSITORI_PATH;
        }
        request = this.http.get<ContentDataHoraModificacioDTO[]>(
          `${this.env.apiBaseUrl}${this.API_PATH}${path}${this.DATA_HORA_MODIFICACIO}${param}`
        );
      }
      request.pipe(take(1)).subscribe({
        next: (data: ContentDataHoraModificacioDTO[]) => {
          //Comparo los datos de últimas actualizaciones con los registros de la indexedDB, eliminando
          // y actualizando los datos necesarios
          if (!data || (states && this.isAdministradorOrResponsable())) {
            observer.next(allContent);
            return;
          }
          let itemsToUpdateOrAddByIds: { id: number; versio: number }[] = [];
          const itemsToDelete: [number, number][] = [];
          const itemsToAdd: [number, number][] = [];
          const itemsToUpdate: [number, number][] = [];
          allContent.forEach((content: ContentDTO) => {
            const foundByPk = data.find((d: ContentDataHoraModificacioDTO) => d.id === content.id && d.versio === content.versio);
            if (
              foundByPk &&
              foundByPk.dataHoraDarreraModificacio !== content.dataHoraDarreraModificacio &&
              foundByPk.dataHoraDarreraModificacio !== content.dataEliminacio
            ) {
              //Se encontró el mismo contenido pero ha sufrido modificaciones
              itemsToUpdateOrAddByIds.push({ id: content.id, versio: content.versio });
              itemsToUpdate.push([content.id, content.versio]);
            } else if (!foundByPk) {
              //Debo eliminarlo de la BBDD
              itemsToDelete.push([content.id, content.versio]);
            }
          });
          itemsToUpdateOrAddByIds = [
            ...itemsToUpdateOrAddByIds,
            ...data
              .filter(
                (d: ContentDataHoraModificacioDTO) =>
                  !allContent.find((content) => content.id === d.id && content.versio === d.versio)
              )
              .map((d) => {
                itemsToAdd.push([d.id, d.versio]);
                return { id: d.id, versio: d.versio };
              })
          ];
          const requests = [];
          if (itemsToDelete.length) {
            //Contenido a eliminar
            requests.push(from(this.progressiveDownloadService.semDB[view].bulkDelete(itemsToDelete)).pipe(take(1)));
          }
          if (itemsToUpdateOrAddByIds.length) {
            //Nuevo contenido a insertar
            //DGDC: en este punto podríamos implementar algo para que la carga de actualizaciones y
            //  nuevos elementos se haga en modo progresivo
            requests.push(this.filterContentByViewAndUpdateIndexedDB(view, { ids: itemsToUpdateOrAddByIds }).pipe(take(1)));
          }
          if (requests.length) {
            this.progressiveDownloadService.updatingContentInformation.next({
              view,
              updates: {
                deleting: itemsToDelete.length,
                updating: itemsToUpdate.length,
                adding: itemsToAdd.length
              }
            });
            forkJoin(requests).subscribe({
              next: () => {
                //Se han hecho las eliminaciones e inserciones necesarias,
                // por lo que podemos devolver la información almacenada en la IndexedDB
                this.progressiveDownloadService.updatingContentInformation.next({ view, updates: null });
                this.progressiveDownloadService.semDB[view].toArray().then((newUpdatedContent: ContentDTO[]) => {
                  observer.next(newUpdatedContent);
                });
              },
              error: (err) => {
                this.progressiveDownloadService.updatingContentInformation.next({ view, updates: null });
                observer.error(err);
              }
            });
          } else {
            //Nada que hacer, devolver datos de la indexed
            observer.next(allContent);
          }
        },
        error: (err) => {
          observer.error(err);
        }
      });
    });
  }

  private isAdministradorOrResponsable(): boolean {
    const userRole = this.authService.getUserRole();
    return userRole === 'ADMINISTRADOR' || userRole === 'RESPONSABLE';
  }

  private filterContentByViewAndUpdateIndexedDB(
    view: ViewTypesConstants,
    filter: { ids: { id: number; versio: number }[]; tipus?: string[] }
  ): Observable<ContentDTO[]> {
    return new Observable((observer) => {
      let viewPath = `/${view}`;
      if (isClinicGuideView(view) || isDirectContentView(view)) {
        viewPath = this.GUIES_CLINIQUES_PATH;
        filter.tipus = [view];
      } else if (view === ViewTypesConstants.codisActivacio) {
        viewPath = this.CODIS_ACTIVACIO_PATH;
      } else if (view === ViewTypesConstants.repositori) {
        viewPath = this.REPOSITORI_PATH;
      }

      if (filter?.ids?.length) {
        this.filterPksThatDontExistInIndexedDB(view, filter.ids).then((ids) => {
          if (ids.length) {
            filter.ids = ids;
          }
        });
      }
      if (filter?.ids?.length === 0) {
        observer.next([]);
        return;
      }

      let url = `${this.env.apiBaseUrl}${this.API_PATH}${viewPath}${this.FILTER_PATH}`;
      //Si el tipo de vista es direct content view añado el parámetro de calidad a la petición
      if (isDirectContentView(view)) {
        if (!this.isAdministradorOrResponsable()) {
          url += `?resolucioImatges=${this.progressiveDownloadService.getRequestParamQuality()}`;
        } else {
          url += `?resolucioImatges=FHD`;
        }
      }

      this.http
        .post<ContentDTO[]>(url, filter)
        .pipe(take(1))
        .subscribe({
          next: (data: ContentDTO[]) => {
            data = data ? data : [];
            data?.forEach((content) => {
              //Se guarda en IndexedDB
              this.progressiveDownloadService.semDB[view]
                .put(content)
                .then(() => {})
                .catch((err) => observer.error(err));
            });
            observer.next(data);
          },
          error: (err) => {
            observer.error(err);
          }
        });
    });
  }

  private async filterPksThatDontExistInIndexedDB(
    view: ViewTypesConstants,
    ids: { id: number; versio: number }[]
  ): Promise<{ id: number; versio: number }[]> {
    await this.progressiveDownloadService.semDB[view]
      .bulkGet([...ids].map((id) => [id.id, id.versio]))
      .then((contents: ContentDTO[]) => {
        ids = ids.filter((id, index) => id.id && contents[index]);
      });
    return ids;
  }
}
